import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Gavel, Warning } from '@mui/icons-material';
import { useAcceptTerms } from '../../hooks/useCommunityMutations';
import { COLORS } from '@elevair/shared';

interface TermsAcceptanceModalProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

export default function TermsAcceptanceModal({
  open,
  onAccept,
  onDecline,
}: TermsAcceptanceModalProps) {
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const acceptTerms = useAcceptTerms();

  const handleAccept = async () => {
    if (!agreed) return;
    setError(null);

    try {
      await acceptTerms.mutateAsync();
      onAccept();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to accept terms');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onDecline}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: `${COLORS.primary}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.primary,
            }}
          >
            <Gavel />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Community Guidelines
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" paragraph>
          Welcome to the Elevair Community Hub! Before you can participate,
          please review and accept our community guidelines.
        </Typography>

        <Box
          sx={{
            bgcolor: 'grey.50',
            borderRadius: 2,
            p: 2,
            mb: 2,
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            By participating in this community, you agree to:
          </Typography>
          <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
            <li>
              <Typography variant="body2">
                <strong>Be Respectful:</strong> Treat all members with kindness and respect.
                No harassment, bullying, or discrimination of any kind.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Keep It Professional:</strong> This is a community for service professionals.
                Keep discussions relevant and constructive.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>No Spam or Self-Promotion:</strong> Excessive self-promotion,
                spam, or unsolicited advertising is not permitted.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Protect Privacy:</strong> Do not share personal information about
                yourself or others, including client details.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>No Inappropriate Content:</strong> Offensive language, explicit content,
                or harmful material will result in immediate removal.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Report Violations:</strong> Help keep the community safe by reporting
                content that violates these guidelines.
              </Typography>
            </li>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: '#FEF3C7',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          <Warning sx={{ color: '#D97706', fontSize: 20, mt: 0.3 }} />
          <Typography variant="body2" color="text.secondary">
            Violation of these guidelines may result in content removal, temporary
            suspension, or permanent ban from the Community Hub.
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to the Community Guidelines
            </Typography>
          }
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onDecline} sx={{ color: 'text.secondary' }}>
          Maybe Later
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!agreed || acceptTerms.isPending}
          sx={{
            backgroundColor: COLORS.accent,
            '&:hover': {
              backgroundColor: COLORS.accent,
              opacity: 0.9,
            },
          }}
        >
          {acceptTerms.isPending ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
              Accepting...
            </>
          ) : (
            'Accept & Continue'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
